import { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import SoftButton from "components/SoftButton";
import {
  deleteFlowTrigger,
  setIsTitlebarTriggerListOpen,
  closeTriggerDeleteDialog,
  clearTriggerForDelete
} from "redux/actions/flowDesigner/triggersActionCreator";
import { TabContextApi } from "layouts/Flow Designer/context";
import { TRIGGER_TYPES } from "constants/flowDesigner";
import { deleteContextTrigger } from "redux/actions/flowDesigner/editorContextActionCreator";
import { deleteScheduleByFlowByFlowId } from "api/FlowDesignerApis";
import { deleteResourceDependency } from "redux/actions/portal/resourceDependenciesActionCreator";
import { getResourceDependencyContextId } from "helpers/resourceDependencies";
import { toast } from "react-toastify";
import { deleteResourceDependencies } from "api/ResourceDependenciesApi";

const TriggerDeleteDialog = () => {
  const { showTriggerDeleteDialog, triggerForDelete } = useSelector(
    (store) => store?.designer?.triggers
  );
  const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
  const dispatch = useDispatch();
  const { autoSaveFlow } = useContext(TabContextApi);

  const handleTriggerDelete = async () => {
    if (triggerForDelete?.type === TRIGGER_TYPES.SCHEDULE) {
      const response = await deleteScheduleByFlowByFlowId(
        currentSavedFlow?.tenant_id,
        currentSavedFlow?.id
      );

      if (!response?.isSuccessful) {
         toast.error("Failed to delete schedule dependencies");
         return;
      }

      const selectedScheduleIds = triggerForDelete?.properties?.schedules || [];

      if (selectedScheduleIds.length) {
        const deleteDependenciesResponse = await deleteResourceDependencies({
          tenantId: currentSavedFlow.tenant_id,
          dependencies: selectedScheduleIds.map((scheduleId) => ({
            sourceId: scheduleId,
            targetId: currentSavedFlow.id,
            sourceResourceType: "schedule",
            targetResourceType: "flow",
            contextId: `${scheduleId}:${currentSavedFlow.id}`
          }))
        });

        if (!deleteDependenciesResponse?.isSuccessful) {
          toast.error("Failed to delete schedule dependencies");
          return;
        }
      }
    }

    const credentialId = triggerForDelete?.properties?.credentials?.value;

    if (
      credentialId &&
      currentSavedFlow?.id &&
      currentSavedFlow?.tenant_id
    ) {
      dispatch(
        deleteResourceDependency({
          tenantId: currentSavedFlow.tenant_id,
          sourceId: currentSavedFlow.id,
          targetId: credentialId,
          sourceResourceType: "flow",
          targetResourceType: "credential",
          contextId: getResourceDependencyContextId(
            currentSavedFlow.id,
            triggerForDelete.type
          )
        })
      );
    }

    dispatch(deleteFlowTrigger());
    dispatch(deleteContextTrigger(triggerForDelete?.label, currentSavedFlow?.name));
    dispatch(setIsTitlebarTriggerListOpen(false));
    autoSaveFlow();
    onClose();
  };

  const onClose = () => {
    dispatch(closeTriggerDeleteDialog());
    setTimeout(() => {
      dispatch(clearTriggerForDelete());
    }, 300);
  };

  return (
    <Dialog open={showTriggerDeleteDialog} onClose={onClose} fullWidth>
      <DialogTitle>Delete Trigger</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Do you want to delete <strong>{triggerForDelete?.label}</strong> trigger?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <SoftButton onClick={onClose} color="primary" variant="outlined">
          Cancel
        </SoftButton>
        <SoftButton onClick={handleTriggerDelete} color="primary">
          Delete
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
};

export default TriggerDeleteDialog;
